import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import AppSpinner from "../Spinner/AppSpinner";
import DataLoader from "../Spinner/DataLoader";
import { Modal, Toast, ToastContainer, Button } from "react-bootstrap";
import { BASE_URL } from "../../constants";
import StudentTab from "../StudentTab/StudentTab";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import SelectionPanelForMarks from "../SelectionPanelForMarks/SelectionPanelForMarks";
import ChangePassword from "../ChangePassword/ChangePassword";
import { apiFetch } from "../../utils/apiFetch";
import ValidationErrorModal from "../ValidationErrorModal/ValidationErrorModal";
import { useApiError } from "../../utils/useApiError";
import {
  formatDateTimeLocalValue,
  toIsoStringKeepingLocalTime,
} from "../../utils/datetime";
import PageInfoModal from "../PageInfoModal/PageInfoModal";

const ACTIVE_LESSON_INFO_ITEMS = [
  {
    icon: "check2-square",
    text: "Hər tələbə üçün davamiyyət (İştirak edir / etmir) və üzrlü/üzrsüz qeyd edin.",
  },
  {
    icon: "person-fill",
    text: "Tələbənin adına klik edərək onu seçin — seçildikdən sonra aşağıdakı əməliyyatlar aktiv olur.",
  },
  {
    icon: "clock-history",
    text: "Tələbəni seçib tarix saatı (⏱) düyməsinə klik etməklə onun keçmiş bal tarixçəsinə baxa bilərsiniz.",
  },
  {
    icon: "key-fill",
    text: "Sarı açar (🔑) düyməsi ilə seçilmiş tələbənin şifrəsini dəyişə bilərsiniz.",
  },
  {
    icon: "megaphone-fill",
    text: "Mavi megafon (📢) düyməsi ilə seçilmiş tələbənin valideynlərinə bildiriş göndərə bilərsiniz.",
  },
  {
    icon: "person-raised-hand",
    text: 'Tələbənin yanındakı checkbox-u işarələyib "Kömək dərsi" düyməsinə klik edərək kömək dərsi planlamaq olar.',
  },
  {
    icon: "send-fill",
    text: '"Davamiyyəti Göndər" (bənövşəyi) düyməsi ilə davamiyyəti rəsmi sistemə göndərin.',
  },
  {
    icon: "bell-fill",
    text: '"Dərs bitdi" (yaşıl) düyməsinə klik edərək valideynlərə dərsin bitməsi barədə avtomatik bildiriş göndərin.',
  },
  {
    icon: "stop-fill",
    text: '"Bitir" (qırmızı) düyməsi bütün qiymət və məlumatları sistemə yazdırır, dərsi sonlandırır.',
  },
  {
    icon: "arrows-move",
    text: "Cədvəl sütunlarını sürükle-yerləşdir (drag & drop) ilə istədiyiniz sıraya düzə bilərsiniz.",
  },
  {
    icon: "floppy-fill",
    text: "Diskette düyməsi ilə sütun sırasını gələcəkdəki dərslər üçün yadda saxlaya bilərsiniz.",
  },
];

const MARK_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const PARENT_NOTIFY_PRESETS = [
  "Tələbə dərsdə telefonla oynayır.",
  "Tələbə dərsdə diqqətsizdir.",
  "Tələbə dərsdə oyun oynayır.",

  "Tələbə dərsdə kompüterdə kənar işlərlə məşğuldur.",
  "Tələbə dərsdə davranış qaydalarını pozub.",
  "Tələbə dərsdə yatır.",
];
const filterOptions = ["Hamısı", "Aktiv", "Fasilə", "Passiv", "Məzun"];

const BADGE_CONFIG = {
  1: { bg: "rgba(5, 150, 105, 0.1)", color: "#059669", border: "#059669" },
  2: { bg: "rgba(245, 158, 11, 0.1)", color: "#f59e0b", border: "#f59e0b" },
  3: { bg: "rgba(239, 68, 68, 0.1)", color: "#ef4444", border: "#ef4444" },
};

const LS_KEY = (groupId) => `activeLessonDraft_${groupId}`;
const PREV_NOTES_KEY = (groupId) => `prevLessonNotes_${groupId}`;

const initialColumns = [
  { id: "index", label: "#" },
  { id: "name", label: "Ad" },
  { id: "surname", label: "Soyad" },
  { id: "phone", label: "Telefon" },
  { id: "username", label: "İstifadəçi adı" },
  { id: "attendance", label: "Davamiyyət" },
  { id: "homework", label: "Ev tapşırığı" },
  { id: "test", label: "Test" },
  { id: "classwork", label: "Sinif işi" },
  { id: "selection", label: "Tələbə qeydi" },
  { id: "note", label: "Müəllim qeydi" },
];

function AttendanceConfirmModal({ show, onHide, onConfirm, loading }) {
  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="sm">
      <Modal.Body style={{ padding: "30px 20px", textAlign: "center" }}>
        <div
          style={{
            width: "60px",
            height: "60px",
            background: "rgba(37, 99, 235, 0.1)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
          }}
        >
          <i
            className="bi bi-send-fill"
            style={{ fontSize: "26px", color: "var(--color-primary)" }}
          ></i>
        </div>
        <h5
          style={{
            fontWeight: 700,
            fontSize: "16px",
            marginBottom: "10px",
            color: "#111827",
          }}
        >
          Davamiyyəti göndərirsiniz?
        </h5>
        <p style={{ color: "#4b5563", fontSize: "13px", lineHeight: "1.5" }}>
          Davamiyyət məlumatları göndəriləcək. Davam etmək istədiyinizə
          əminsiniz?
        </p>
        <div style={{ display: "flex", gap: "10px", marginTop: "25px" }}>
          <Button
            variant="light"
            onClick={onHide}
            disabled={loading}
            style={{
              flex: 1,
              fontSize: "13px",
              fontWeight: 600,
              borderRadius: "8px",
              padding: "10px",
            }}
          >
            Ləğv et
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 1,
              fontSize: "13px",
              fontWeight: 600,
              borderRadius: "8px",
              padding: "10px",
              background: "var(--color-primary)",
              border: "none",
              color: "#fff",
            }}
          >
            {loading ? <AppSpinner inline /> : "Bəli, göndər"}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function EndLessonConfirmModal({ show, onHide, onConfirm, loading }) {
  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="sm">
      <Modal.Body style={{ padding: "30px 20px", textAlign: "center" }}>
        <div
          style={{
            width: "60px",
            height: "60px",
            background: "rgba(239, 68, 68, 0.1)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
          }}
        >
          <i
            className="bi bi-exclamation-circle"
            style={{ fontSize: "30px", color: "#ef4444" }}
          ></i>
        </div>
        <h5
          style={{
            fontWeight: 700,
            fontSize: "16px",
            marginBottom: "10px",
            color: "#111827",
          }}
        >
          Dərsi bitirirsiniz?
        </h5>
        <p style={{ color: "#4b5563", fontSize: "13px", lineHeight: "1.5" }}>
          Bu hərəkət dərsi rəsmi olaraq sonlandıracaq və qiymətləri yadda
          saxlayacaq. Davam etmək istədiyinizə əminsiniz?
        </p>
        <div style={{ display: "flex", gap: "10px", marginTop: "25px" }}>
          <Button
            variant="light"
            onClick={onHide}
            disabled={loading}
            style={{
              flex: 1,
              fontSize: "13px",
              fontWeight: 600,
              borderRadius: "8px",
              padding: "10px",
            }}
          >
            Ləğv et
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 1,
              fontSize: "13px",
              fontWeight: 600,
              borderRadius: "8px",
              padding: "10px",
              background: "#ef4444",
              border: "none",
            }}
          >
            {loading ? <AppSpinner inline /> : "Bəli, bitir"}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function SelectionModal({ show, onHide, student, onSelectMark, prevNotes }) {
  if (!show) return null;
  return ReactDOM.createPortal(
    <div
      role="presentation"
      onClick={onHide}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        background: "rgba(15, 23, 42, 0.45)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          border: "1.5px solid var(--color-primary)",
          borderRadius: 14,
          boxShadow:
            "0 24px 64px rgba(37,99,235,0.22), 0 4px 16px rgba(0,0,0,0.14)",
          width: 480,
          maxWidth: "94vw",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "18px 22px 14px",
            borderBottom: "1px solid #f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "var(--color-primary)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              Tələbə qeydi
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
              <i
                className="bi bi-person-fill"
                style={{ marginRight: 5, color: "#9ca3af" }}
              ></i>
              {student?.name} {student?.surname}
            </div>
          </div>
          <button
            type="button"
            onClick={onHide}
            style={{
              border: "none",
              background: "rgba(0,0,0,0.06)",
              borderRadius: 6,
              width: 28,
              height: 28,
              fontSize: 16,
              color: "#6b7280",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "14px 22px" }}>
          <SelectionPanelForMarks
            onSelect={(template) => {
              onSelectMark(
                student.id,
                template.id,
                template.name,
                template.noteLevel,
              );
            }}
            disabledNotes={prevNotes}
          />
        </div>
        <div
          style={{
            padding: "12px 22px",
            borderTop: "1px solid #f3f4f6",
            display: "flex",
            justifyContent: "flex-end",
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onHide}
            style={{
              border: "1.5px solid #d1d5db",
              borderRadius: 7,
              padding: "7px 18px",
              background: "#f9fafb",
              color: "#374151",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Bağla
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SortableCol({ col, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: col.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <th
      ref={setNodeRef}
      style={{
        ...style,
        padding: "8px 8px",
        textAlign: "center",
        borderBottom: "1px solid var(--border)",
        background: "#f3f4f6",
        cursor: "grab",
        userSelect: "none",
        whiteSpace: "nowrap",
        fontSize: 11,
        color: "#374151",
        fontWeight: 700,
        letterSpacing: "0.01em",
      }}
      {...attributes}
      {...listeners}
    >
      {col.id === "selection" ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span>{children}</span>
        </div>
      ) : (
        children
      )}
    </th>
  );
}

function MarkSelect({ value, onChange, disabled }) {
  const bg = disabled
    ? "#e5e7eb"
    : value >= 8
      ? "#059669"
      : value >= 5
        ? "#f59e0b"
        : "#ef4444";
  const textColor = disabled ? "#9ca3af" : "#ffffff";
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={disabled}
      style={{
        border: "none",
        borderRadius: 6,
        fontWeight: 700,
        fontSize: 13,
        padding: "4px 22px 4px 8px",
        width: 68,
        minWidth: 68,
        maxWidth: 68,
        background: bg,
        color: textColor,
        cursor: disabled ? "not-allowed" : "pointer",
        textAlign: "center",
        appearance: "auto",
        WebkitAppearance: "auto",
        MozAppearance: "auto",
      }}
    >
      {MARK_OPTIONS.map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
  );
}

function NoteCell({
  value,
  onChange,
  disabled,
  studentName,
  voice = null,
  onVoiceChange = () => {},
  onError = () => {},
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value || "");
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [localVoicePreview, setLocalVoicePreview] = useState(null);
  const [blinkOn, setBlinkOn] = useState(true);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const blinkRef = useRef(null);

  useEffect(() => {
    setText(value || "");
  }, [value]);

  useEffect(() => {
    if (recording) {
      blinkRef.current = setInterval(() => setBlinkOn((v) => !v), 500);
    } else {
      clearInterval(blinkRef.current);
      setBlinkOn(true);
    }
    return () => clearInterval(blinkRef.current);
  }, [recording]);

  useEffect(() => {
    return () => {
      setLocalVoicePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  const clearLocalPreview = () => {
    setLocalVoicePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      clearInterval(timerRef.current);
      setRecording(false);
    }
  };

  const handleClose = () => {
    if (recording) stopRecording();
    clearLocalPreview();
    setText(value || "");
    setOpen(false);
  };

  const handleSave = () => {
    onChange(text);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") handleClose();
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  const formatTime = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const previewUrl = URL.createObjectURL(blob);
        setLocalVoicePreview((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return previewUrl;
        });
        setUploading(true);
        try {
          const formData = new FormData();
          formData.append("file", blob, "teacher-voice.webm");
          const res = await apiFetch(`${BASE_URL}/files/upload-teacher-file`, {
            method: "POST",
            body: formData,
          });
          const data = await res.json();
          if (data.fileUuidName) onVoiceChange(data.fileUuidName);
        } catch (err) {
          onError("Səs yüklənərkən xəta baş verdi.");
        } finally {
          setUploading(false);
        }
      };
      mr.start();
      setRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(
        () => setRecordingTime((t) => t + 1),
        1000,
      );
    } catch (err) {
      onError(
        "Mikrofon icazəsi alınmadı. Zəhmət olmasa brauzerdə icazə verin.",
      );
    }
  };

  const hasVoice = !!localVoicePreview || !!voice;
  const voiceSrc =
    localVoicePreview || (voice ? `${BASE_URL}/files/download/${voice}` : null);
  const hasNote = !!text || hasVoice;

  return (
    <>
      <button
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        title={text || "Qeyd yaz..."}
        style={{
          border: hasNote
            ? "1.5px solid rgba(37,99,235,0.4)"
            : "1.5px dashed #d1d5db",
          borderRadius: 6,
          padding: "3px 8px",
          background: hasNote ? "rgba(37,99,235,0.06)" : "transparent",
          color: hasNote ? "var(--color-primary)" : "#9ca3af",
          cursor: disabled ? "not-allowed" : "pointer",
          fontSize: 11,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
          transition: "all 0.15s",
          maxWidth: 120,
          overflow: "hidden",
        }}
      >
        <i
          className="bi bi-chat-left-text-fill"
          style={{ flexShrink: 0, fontSize: 11 }}
        ></i>
        {text && (
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 80,
              fontSize: 11,
            }}
          >
            {text}
          </span>
        )}
      </button>

      {open &&
        ReactDOM.createPortal(
          <>
            <style>{`@keyframes notecell-spin{to{transform:rotate(360deg)}}`}</style>
            <div
              role="presentation"
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 100050,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                background: "rgba(15, 23, 42, 0.45)",
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                style={{
                  background: "#fff",
                  border: "1.5px solid var(--color-primary)",
                  borderRadius: 14,
                  boxShadow:
                    "0 24px 64px rgba(37,99,235,0.22), 0 4px 16px rgba(0,0,0,0.14)",
                  padding: "22px 24px",
                  width: 520,
                  maxWidth: "95vw",
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                }}
              >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--color-primary)",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      Müəllim qeydi
                    </div>
                    {studentName && (
                      <div
                        style={{
                          fontSize: 12,
                          color: "#6b7280",
                          fontWeight: 500,
                        }}
                      >
                        <i
                          className="bi bi-person-fill"
                          style={{ marginRight: 5, color: "#9ca3af" }}
                        ></i>
                        {studentName}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleClose}
                    style={{
                      border: "none",
                      background: "rgba(0,0,0,0.06)",
                      borderRadius: 6,
                      width: 28,
                      height: 28,
                      fontSize: 16,
                      color: "#6b7280",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Info */}
                <div
                  style={{
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: 7,
                    padding: "10px 14px",
                    fontSize: 12,
                    color: "#1e40af",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <i
                    className="bi bi-info-circle-fill"
                    style={{ marginTop: 2, flexShrink: 0 }}
                  ></i>
                  <span
                    style={{
                      lineHeight: 1.8,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    Qeyd Azərbaycan dilinin orfoqrafiyasına uyğun yazılmalıdır.
                    Məs: <strong>yaxşı</strong> (yaxshi yox),{" "}
                    <strong>işləmək</strong> (islemek yox)
                  </span>
                </div>

                {/* Textarea — Enter saves, Shift+Enter new line */}
                <textarea
                  autoFocus
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={5}
                  placeholder="Qeyd yazın... (Enter — saxla, Shift+Enter — yeni sətir)"
                  style={{
                    width: "100%",
                    border: "1.5px solid #d1d5db",
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontSize: 13,
                    color: "#111827",
                    resize: "vertical",
                    outline: "none",
                    lineHeight: 1.7,
                    fontFamily: "inherit",
                    minHeight: 110,
                    transition: "border 0.15s",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "var(--color-primary)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";
                  }}
                />

                {/* Voice section */}
                <div
                  style={{
                    borderTop: "1px solid #f3f4f6",
                    paddingTop: 14,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#374151",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <i
                      className="bi bi-mic-fill"
                      style={{ color: "#ef4444", fontSize: 13 }}
                    />
                    Səs qeydi
                  </div>

                  {hasVoice ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        background: "#f5f3ff",
                        border: "1px solid #ede9fe",
                        borderRadius: 8,
                        padding: "8px 12px",
                      }}
                    >
                      <audio
                        controls
                        src={voiceSrc}
                        style={{ flex: 1, height: 32 }}
                      />
                      <button
                        onClick={() => {
                          clearLocalPreview();
                          onVoiceChange(null);
                        }}
                        title="Səsi sil"
                        style={{
                          border: "1.5px solid #fca5a5",
                          borderRadius: 6,
                          background: "#fef2f2",
                          color: "#ef4444",
                          width: 28,
                          height: 28,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        <i className="bi bi-trash-fill" />
                      </button>
                    </div>
                  ) : uploading ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 12,
                        color: "#6b7280",
                      }}
                    >
                      <div
                        style={{
                          width: 14,
                          height: 14,
                          border: "2px solid var(--color-primary)",
                          borderTopColor: "transparent",
                          borderRadius: "50%",
                          animation: "notecell-spin 0.7s linear infinite",
                        }}
                      />
                      Yüklənir...
                    </div>
                  ) : recording ? (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <div
                        style={{
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          background: "#ef4444",
                          opacity: blinkOn ? 1 : 0.2,
                          transition: "opacity 0.1s",
                        }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          color: "#ef4444",
                          fontWeight: 600,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatTime(recordingTime)}
                      </span>
                      <button
                        onClick={stopRecording}
                        style={{
                          border: "none",
                          borderRadius: 6,
                          padding: "5px 14px",
                          background: "#ef4444",
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <i className="bi bi-stop-fill" /> Dayandır
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={startRecording}
                      style={{
                        alignSelf: "flex-start",
                        border: "1.5px solid #ef4444",
                        borderRadius: 7,
                        padding: "6px 14px",
                        background: "#fef2f2",
                        color: "#ef4444",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <i className="bi bi-mic-fill" /> Səs yaz
                    </button>
                  )}
                </div>

                {/* Footer */}
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={handleClose}
                    style={{
                      border: "1.5px solid #d1d5db",
                      borderRadius: 7,
                      padding: "7px 18px",
                      background: "#f9fafb",
                      color: "#374151",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Ləğv
                  </button>
                  <button
                    onClick={handleSave}
                    style={{
                      border: "none",
                      borderRadius: 7,
                      padding: "7px 20px",
                      background: "var(--color-primary)",
                      color: "white",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <i className="bi bi-check-lg"></i> Saxla
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

export default function ActiveLessonTab({
  groupId,
  styles: s,
  onFinish,
  onEndLesson,
  lessonName,
  lessonDate,
  materialLink,
  videoLink,
  trainingType,
  lessonType: propLessonType,
  groupData,
  onCellClick,
}) {
  const [activeFilter, setActiveFilter] = useState("Aktiv");
  const [students, setStudents] = useState([]);
  const [groupInfo, setGroupInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef([]);
  const [saving, setSaving] = useState(false);
  const [endingLesson, setEndingLesson] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [lessonDoneLoading, setLessonDoneLoading] = useState(false);

  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [viewingHistory, setViewingHistory] = useState(false);

  const [generalNote, setGeneralNote] = useState("");

  const [lessonType, setLessonType] = useState(propLessonType || "Standart");
  const [attendance, setAttendance] = useState({});
  // excusedReasons: { [studentId]: true (üzrlü) | false (üzrsüz) }
  const [excusedReasons, setExcusedReasons] = useState({});
  const [homework, setHomework] = useState({});
  const [test, setTest] = useState({});
  const [classwork, setClasswork] = useState({});
  const [studentNotes, setStudentNotes] = useState({});
  const [manualNotes, setManualNotes] = useState({});
  const [manualVoices, setManualVoices] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalStudent, setModalStudent] = useState(null);
  const [prevLessonNotes, setPrevLessonNotes] = useState({});

  const [columns, setColumns] = useState(initialColumns);
  const [activeCol, setActiveCol] = useState(null);
  const [saveStatus, setSaveStatus] = useState(false);

  const [expandedRows, setExpandedRows] = useState({});

  const [showAttendanceConfirm, setShowAttendanceConfirm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordStudent, setPasswordStudent] = useState(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitReason, setExitReason] = useState("");
  const [exitLoading, setExitLoading] = useState(false);

  const [showNotifyParentModal, setShowNotifyParentModal] = useState(false);
  const [notifyParentMessage, setNotifyParentMessage] = useState([]);
  const [notifyParentLoading, setNotifyParentLoading] = useState(false);

  const [helpSelectedIds, setHelpSelectedIds] = useState(new Set());
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpNotes, setHelpNotes] = useState({});
  const [helpLoading, setHelpLoading] = useState(false);
  const [helpBeginDate, setHelpBeginDate] = useState("");
  const [helpEndDate, setHelpEndDate] = useState("");
  const [helpLessonName, setHelpLessonName] = useState("");
  const [helpYoutubeLink, setHelpYoutubeLink] = useState("");
  const [helpLessonType, setHelpLessonType] = useState("Online");
  const [helpErrorModal, setHelpErrorModal] = useState(null);

  const toggleHelpSelect = (studentId) => {
    setHelpSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(studentId) ? next.delete(studentId) : next.add(studentId);
      return next;
    });
  };

  const helpNow = () => formatDateTimeLocalValue();

  const openHelpModal = () => {
    const n = helpNow();
    setHelpBeginDate(n);
    setHelpEndDate(n);
    setHelpNotes({});
    setHelpLessonName("");
    setHelpYoutubeLink("");
    setHelpLessonType("Online");
    setShowHelpModal(true);
  };

  const closeHelpModal = () => {
    setShowHelpModal(false);
    setHelpNotes({});
  };

  const helpSelectedStudents = students.filter((st) =>
    helpSelectedIds.has(st.id),
  );

  const handleHelpSubmit = async () => {
    setHelpLoading(true);
    try {
      const res = await apiFetch(`${BASE_URL}/teacher-help-lessons`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          groupId: Number(groupId),
          beginDate: toIsoStringKeepingLocalTime(helpBeginDate),
          endDate: toIsoStringKeepingLocalTime(helpEndDate),
          lessonName: helpLessonName.trim(),
          youtubeLink: helpYoutubeLink.trim(),
          lessonType: helpLessonType,
          students: helpSelectedStudents.map((st) => ({
            contractId: st.contractId,
            teacherNotes: (helpNotes[st.contractId] || "").trim(),
          })),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setHelpErrorModal({
          message: data?.message ?? "Xəta baş verdi",
          validations: data?.validations ?? data?.errors ?? null,
        });
        return;
      }
      showToast("Kömək dərsi yadda saxlandı", "success");
      setHelpSelectedIds(new Set());
      closeHelpModal();
    } catch {
      setHelpErrorModal({
        message: "Şəbəkə xətası baş verdi",
        validations: null,
      });
    } finally {
      setHelpLoading(false);
    }
  };

  const handleSubmitExitReason = async () => {
    const st = students.find((s) => s.id === selectedStudentId);
    if (!exitReason.trim() || !st?.contractId) return;
    setExitLoading(true);
    try {
      const res = await apiFetch(`${BASE_URL}/contracts/add-exit-reason`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contractId: st.contractId,
          reason: exitReason.trim(),
        }),
      });
      if (!res.ok) throw new Error();
      setShowExitModal(false);
      setExitReason("");
    } catch {
      showToast("Səbəb göndərilərkən xəta baş verdi", "danger");
    } finally {
      setExitLoading(false);
    }
  };

  const handleSendParentNotification = async () => {
    const st = students.find((s) => s.id === selectedStudentId);
    if (!notifyParentMessage.length || !st?.contractId) return;
    setNotifyParentLoading(true);
    try {
      const res = await apiFetch(
        `${BASE_URL}/mobile/notifications/send-to-student-parents`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contractId: st.contractId,
            body: notifyParentMessage.join("\n"),
          }),
        },
      );
      if (res.ok) {
        setShowNotifyParentModal(false);
        setNotifyParentMessage("");
        setTimeout(
          () => showToast("Valideynə bildiriş uğurla göndərildi!", "success"),
          150,
        );
      } else {
        const data = await res.json().catch(() => ({}));
        handleApiError({
          message:
            data?.message ?? "Bildiriş göndərilərkən server xətası baş verdi.",
          validations: data?.validations,
        });
      }
    } catch {
      handleApiError({ message: "Bildiriş göndərilərkən xəta baş verdi." });
    } finally {
      setNotifyParentLoading(false);
    }
  };

  const tableScrollRef = useRef(null);
  const customScrollRef = useRef(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const syncingRef = useRef(false);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const update = () => setTableScrollWidth(el.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      if (syncingRef.current) return;
      if (customScrollRef.current) {
        syncingRef.current = true;
        customScrollRef.current.scrollLeft = el.scrollLeft;
        syncingRef.current = false;
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const handleCustomScrollX = () => {
    if (syncingRef.current) return;
    const el = tableScrollRef.current;
    if (el && customScrollRef.current) {
      syncingRef.current = true;
      el.scrollLeft = customScrollRef.current.scrollLeft;
      syncingRef.current = false;
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleCellClick = (text) => {
    if (!text) return;
    onCellClick?.(text);
  };

  const showToast = useCallback((message, type = "success") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    const timer = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 2000);
    toastTimers.current.push(timer);
  }, []);

  useEffect(() => {
    const timers = toastTimers.current;
    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  const { errorModal, clearError, handleApiError } = useApiError();

  const showSuccessToast = useCallback(
    (msg) => showToast(msg, "success"),
    [showToast],
  );

  const saveDraft = useCallback(
    (att, excused, hw, tst, cw, nts, mnts, mvcs, gNote) => {
      if (!groupId) return;
      try {
        localStorage.setItem(
          LS_KEY(groupId),
          JSON.stringify({
            attendance: att,
            excusedReasons: excused,
            homework: hw,
            test: tst,
            classwork: cw,
            studentNotes: nts,
            manualNotes: mnts,
            manualVoices: mvcs,
            generalNote: gNote,
          }),
        );
      } catch (e) {
        console.error(e);
      }
    },
    [groupId],
  );

  useEffect(() => {
    if (students.length === 0) return;
    saveDraft(
      attendance,
      excusedReasons,
      homework,
      test,
      classwork,
      studentNotes,
      manualNotes,
      manualVoices,
      generalNote,
    );
  }, [
    attendance,
    excusedReasons,
    homework,
    test,
    classwork,
    studentNotes,
    manualNotes,
    manualVoices,
    generalNote,
    saveDraft,
    students.length,
  ]);

  const handleSelectMark = (studentId, noteId, noteName, noteLevel) => {
    setStudentNotes((prev) => {
      const currentList = prev[studentId] || [];
      if (!currentList.some((n) => n.text === noteName)) {
        return {
          ...prev,
          [studentId]: [
            ...currentList,
            { id: noteId, text: noteName, level: noteLevel },
          ],
        };
      }
      return prev;
    });
  };

  const removeOneNote = (studentId, noteToRemove) => {
    setStudentNotes((prev) => ({
      ...prev,
      [studentId]: (prev[studentId] || []).filter(
        (n) => n.text !== noteToRemove,
      ),
    }));
  };

  const getPayload = () => {
    const finalDate = lessonDate
      ? new Date(
          new Date(lessonDate).getTime() -
            new Date(lessonDate).getTimezoneOffset() * 60000,
        ).toISOString()
      : new Date(
          new Date().getTime() - new Date().getTimezoneOffset() * 60000,
        ).toISOString();
    const marksArray = students.map((st) => ({
      contractId: st.contractId || 0,
      markClasswork: Number(classwork[st.id] ?? 5),
      markTest: Number(test[st.id] ?? 5),
      markHomework: Number(homework[st.id] ?? 5),
      markNoteIds: (studentNotes[st.id] || []).map((n) => n.id).filter(Boolean),
      present: attendance[st.id],
      studentId: st.studentId,
      teacherNotes: manualNotes[st.id] || "",
      teacherVoice: manualVoices[st.id] || null,
    }));

    return {
      marks: marksArray,
      lectures: [],
      teacherSelectedDate: finalDate,
      teacherGroupId: Number(groupId),
      lessonNotes: lessonName || generalNote || "",
      trainingType: trainingType || "Online",
      lessonType: propLessonType || lessonType || "Standart",
      resourceLink: materialLink || "",
      youtubeLink: videoLink || "",
    };
  };

  const getAttendancePayload = () => ({
    attendances: students.map((st) => ({
      studentId: st.studentId,
      present: attendance[st.id],
      // İştirak edərsə excusedReason = true, etməzsə seçilmiş dəyər (default false = üzrsüz)
      excusedReason: attendance[st.id]
        ? true
        : (excusedReasons[st.id] ?? false),
      contractId: st.contractId || 0,
    })),
    trainingType: trainingType || "Online",
    lessonType: propLessonType || lessonType || "Standart",
    trainingName: lessonName || generalNote || "",
    groupName: groupInfo?.groupName || groupData?.groupName || "",
    teacherGroupId: Number(groupId),
  });

  const handleLessonDoneNotification = async () => {
    setLessonDoneLoading(true);
    try {
      const res = await apiFetch(
        `${BASE_URL}/teacher-groups/lesson-done-for-notification`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            teacherGroupId: Number(groupId),
            traningType: trainingType || "Online",
            lessonType: propLessonType || lessonType || "Standart",
          }),
        },
      );
      if (res.ok) {
        showToast("Bildiriş uğurla göndərildi!", "success");
      } else {
        const data = await res.json().catch(() => ({}));
        handleApiError({
          message:
            data?.message ?? "Bildiriş göndərilərkən server xətası baş verdi.",
          validations: data?.validations,
        });
      }
    } catch (err) {
      handleApiError({ message: "Bildiriş göndərilərkən xəta baş verdi." });
    } finally {
      setLessonDoneLoading(false);
    }
  };

  const handleEndLesson = async () => {
    setEndingLesson(true);
    try {
      const payload = getPayload();

      const res = await apiFetch(
        BASE_URL + `/student-marks/add-group-student-marks/v2`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (res.ok) {
        localStorage.setItem(
          PREV_NOTES_KEY(groupId),
          JSON.stringify(studentNotes),
        );
        localStorage.removeItem(LS_KEY(groupId));
        setShowEndConfirm(false);
        if (onEndLesson) onEndLesson();
      } else {
        const data = await res.json().catch(() => ({}));
        setShowEndConfirm(false);
        handleApiError({
          message:
            data?.message ?? "Dərsi sonlandırarkən server xətası baş verdi.",
          validations: data?.validations,
        });
      }
    } catch (err) {
      setShowEndConfirm(false);
    } finally {
      setEndingLesson(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem("activeLessonTableOrder");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.some((c) => c.id === "fullName")) setColumns(initialColumns);
        else setColumns(parsed);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const [groupRes, res] = await Promise.all([
        apiFetch(`${BASE_URL}/teacher-groups/${groupId}`),
        apiFetch(BASE_URL + "/teacher-groups/get-teacher-one-group-students", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            groupId,
            status: activeFilter === "Hamısı" ? "" : activeFilter,
          }),
        }),
        new Promise((r) => setTimeout(r, 600)),
      ]);
      const groupDataApi = await groupRes.json();
      setGroupInfo(groupDataApi.teacherGroup);

      const data = await res.json();
      const list = data.teacherGroupStudents || [];
      setStudents(list);

      const prevRaw = localStorage.getItem(PREV_NOTES_KEY(groupId));
      if (prevRaw) {
        try {
          setPrevLessonNotes(JSON.parse(prevRaw));
        } catch (e) {
          console.error(e);
        }
      }

      const draftRaw = localStorage.getItem(LS_KEY(groupId));
      if (draftRaw) {
        try {
          const draft = JSON.parse(draftRaw);
          const draftAtt = draft.attendance || {};
          const draftExcused = draft.excusedReasons || {};
          const draftHw = draft.homework || {};
          const draftTst = draft.test || {};
          const draftCw = draft.classwork || {};
          list.forEach((st) => {
            if (draftAtt[st.id] === undefined) draftAtt[st.id] = false;
            if (draftExcused[st.id] === undefined) draftExcused[st.id] = false;
            if (draftHw[st.id] === undefined) draftHw[st.id] = 5;
            if (draftTst[st.id] === undefined) draftTst[st.id] = 5;
            if (draftCw[st.id] === undefined) draftCw[st.id] = 5;
          });
          setAttendance(draftAtt);
          setExcusedReasons(draftExcused);
          setHomework(draftHw);
          setTest(draftTst);
          setClasswork(draftCw);
          setStudentNotes(draft.studentNotes || {});
          setManualNotes(draft.manualNotes || {});
          setManualVoices(draft.manualVoices || {});
          setGeneralNote(draft.generalNote || "");
          return;
        } catch (e) {
          console.error(e);
        }
      }

      const att = {},
        excused = {},
        hw = {},
        tst = {},
        cw = {},
        nts = {},
        mnts = {};
      list.forEach((st) => {
        att[st.id] = false;
        excused[st.id] = false; // default üzrsüz
        hw[st.id] = 5;
        tst[st.id] = 5;
        cw[st.id] = 5;
        nts[st.id] = [];
        mnts[st.id] = "";
      });
      setAttendance(att);
      setExcusedReasons(excused);
      setHomework(hw);
      setTest(tst);
      setClasswork(cw);
      setStudentNotes(nts);
      setManualNotes(mnts);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [groupId, activeFilter]);

  useEffect(() => {
    if (groupId) fetchStudents();
  }, [groupId, fetchStudents]);

  const handleSaveMarks = async () => {
    setShowAttendanceConfirm(false);
    setSaving(true);

    try {
      const attendancePayload = getAttendancePayload();
      const attRes = await apiFetch(
        BASE_URL + `/teacher-groups/send-group-attendance`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(attendancePayload),
        },
      );

      if (!attRes.ok)
        throw new Error("Davamiyyət göndərilərkən xəta baş verdi.");

      showSuccessToast("Davamiyyət uğurla göndərildi!");
    } catch (err) {
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (event) =>
    setActiveCol(columns.find((c) => c.id === event.active.id));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveCol(null);
    if (active.id !== over?.id) {
      setColumns((cols) => {
        const oldIndex = cols.findIndex((c) => c.id === active.id);
        const newIndex = cols.findIndex((c) => c.id === over.id);
        const newOrder = arrayMove(cols, oldIndex, newIndex);
        return newOrder;
      });
    }
  };

  const handleAttendanceToggle = (stId) => {
    const newVal = !attendance[stId];
    setAttendance((p) => ({ ...p, [stId]: newVal }));
    if (!newVal) {
      setTest((p) => ({ ...p, [stId]: 5 }));
      setClasswork((p) => ({ ...p, [stId]: 5 }));
      setExcusedReasons((p) => ({ ...p, [stId]: false }));
    }
  };

  const renderCell = (colId, st, idx) => {
    const isSelected = selectedStudentId === st.id;
    switch (colId) {
      case "phone":
      case "username":
        return (
          <td
            key={colId}
            onClick={() => handleCellClick(st[colId] || "")}
            style={{
              padding: "7px 8px",
              fontSize: 12,
              color: "#374151",
              textAlign: "center",
              cursor: "pointer",
            }}
          >
            {st[colId] || "—"}
          </td>
        );
      case "index":
        return (
          <td
            key={colId}
            style={{
              padding: "7px 8px",
              width: 30,
              fontSize: 12,
              color: "#6b7280",
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            {idx + 1}
          </td>
        );
      case "name":
      case "surname":
        return (
          <td
            key={colId}
            onClick={() => {
              setSelectedStudentId(isSelected ? null : st.id);
              handleCellClick(st[colId] || "");
            }}
            style={{
              padding: "7px 8px",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: 13,
              color: isSelected ? "var(--color-primary)" : "#111827",
              textAlign: "center",
            }}
          >
            {st[colId]}
          </td>
        );

      case "attendance": {
        const isPresent = attendance[st.id];
        const isExcused = excusedReasons[st.id];
        return (
          <td
            key={colId}
            style={{ padding: "7px 8px", width: 130, textAlign: "center" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                alignItems: "center",
              }}
            >
              <button
                onClick={() => handleAttendanceToggle(st.id)}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 20,
                  padding: "5px 8px",
                  fontSize: 11,
                  fontWeight: 700,
                  background: isPresent ? "#059669" : "#ef4444",
                  color: "#ffffff",
                  cursor: "pointer",
                  letterSpacing: "0.01em",
                  transition: "background 0.2s",
                }}
              >
                {isPresent ? "İştirak edir" : "İştirak etmir"}
              </button>

              {!isPresent && (
                <div style={{ display: "flex", gap: 4, width: "100%" }}>
                  {[
                    { val: true, label: "Üzrlü", color: "#059669" },
                    { val: false, label: "Üzrsüz", color: "#ef4444" },
                  ].map((opt) => (
                    <button
                      key={String(opt.val)}
                      onClick={() =>
                        setExcusedReasons((p) => ({ ...p, [st.id]: opt.val }))
                      }
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 4,
                        border: `1.5px solid ${opt.color}`,
                        borderRadius: 20,
                        padding: "3px 0",
                        fontSize: 10,
                        fontWeight: 700,
                        background: isExcused === opt.val ? opt.color : "#fff",
                        color: isExcused === opt.val ? "#fff" : opt.color,
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background:
                            isExcused === opt.val ? "#fff" : opt.color,
                          flexShrink: 0,
                        }}
                      />
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </td>
        );
      }

      case "homework":
      case "test":
      case "classwork":
        const isDis = colId !== "homework" && !attendance[st.id];
        const val =
          colId === "homework"
            ? homework[st.id]
            : colId === "test"
              ? test[st.id]
              : classwork[st.id];
        const setVal =
          colId === "homework"
            ? setHomework
            : colId === "test"
              ? setTest
              : setClasswork;
        return (
          <td key={colId} style={{ padding: "7px 8px", textAlign: "center" }}>
            <MarkSelect
              value={val}
              onChange={(v) => setVal((p) => ({ ...p, [st.id]: v }))}
              disabled={isDis}
            />
          </td>
        );

      case "selection":
        const notes = studentNotes[st.id] || [];
        const isRowExpanded = !!expandedRows[st.id];
        const visibleNotes = isRowExpanded ? notes : notes.slice(0, 1);
        const hiddenCount = notes.length - 1;

        return (
          <td
            key={colId}
            style={{
              padding: "6px 8px",
              width: 240,
              minWidth: 240,
              maxWidth: 240,
              verticalAlign: "middle",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 4,
                alignItems: "center",
                overflow: "hidden",
              }}
            >
              <button
                onClick={() => {
                  setModalStudent(st);
                  setShowModal(true);
                }}
                style={{
                  border: "1px dashed var(--color-primary)",
                  borderRadius: 5,
                  padding: "2px 7px",
                  background: "white",
                  color: "var(--color-primary)",
                  cursor: "pointer",
                  fontSize: 10,
                  fontWeight: 600,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  lineHeight: "18px",
                }}
              >
                <i className="bi bi-plus-circle"></i> Seç
              </button>

              <div
                style={{
                  display: "flex",
                  flexDirection: isRowExpanded ? "column" : "row",
                  flexWrap: isRowExpanded ? "nowrap" : "nowrap",
                  gap: 3,
                  overflow: "hidden",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {visibleNotes.map((noteObj, i) => {
                  const config = BADGE_CONFIG[noteObj.level] || BADGE_CONFIG[2];
                  return (
                    <span
                      key={i}
                      style={{
                        background: config.bg,
                        color: config.color,
                        border: `1px solid ${config.border}`,
                        borderRadius: 4,
                        fontSize: 10,
                        fontWeight: 600,
                        padding: "2px 6px",
                        lineHeight: "18px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 110,
                        }}
                      >
                        {noteObj.text}
                      </span>
                      <i
                        className="bi bi-x"
                        style={{
                          cursor: "pointer",
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                        onClick={() => removeOneNote(st.id, noteObj.text)}
                      ></i>
                    </span>
                  );
                })}
              </div>

              {notes.length > 1 && (
                <button
                  onClick={() =>
                    setExpandedRows((prev) => ({
                      ...prev,
                      [st.id]: !prev[st.id],
                    }))
                  }
                  title={isRowExpanded ? "Yığ" : `${hiddenCount} daha`}
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 5,
                    padding: "1px 5px",
                    background: isRowExpanded
                      ? "rgba(37,99,235,0.1)"
                      : "#f9fafb",
                    color: isRowExpanded ? "var(--color-primary)" : "#6b7280",
                    cursor: "pointer",
                    fontSize: 10,
                    fontWeight: 700,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    lineHeight: "18px",
                    transition: "all 0.15s",
                  }}
                >
                  {isRowExpanded ? (
                    <i className="bi bi-chevron-up"></i>
                  ) : (
                    <>
                      <i className="bi bi-chevron-down"></i> +{hiddenCount}
                    </>
                  )}
                </button>
              )}
            </div>
          </td>
        );

      case "note":
        return (
          <td
            key={colId}
            style={{
              padding: "7px 8px",
              width: 130,
              minWidth: 130,
              textAlign: "center",
            }}
          >
            <NoteCell
              value={manualNotes[st.id]}
              onChange={(v) => setManualNotes((p) => ({ ...p, [st.id]: v }))}
              voice={manualVoices[st.id] || null}
              onVoiceChange={(v) =>
                setManualVoices((p) => ({ ...p, [st.id]: v }))
              }
              onError={(msg) => showToast(msg, "danger")}
              studentName={`${st.name} ${st.surname}`}
            />
          </td>
        );
      default:
        return <td key={colId}></td>;
    }
  };

  if (loading)
    return (
      <div style={{ position: "relative", flex: 1, minHeight: 120 }}>
        <DataLoader show />
      </div>
    );

  if (viewingHistory) {
    const selectedStudentObj = students.find(
      (st) => st.id === selectedStudentId,
    );
    return (
      <StudentTab
        groupId={groupId}
        styles={s}
        initialStudent={selectedStudentObj}
        onBack={() => setViewingHistory(false)}
        onCellClick={onCellClick}
      />
    );
  }

  return (
    <div
      className={s.tabWrapper}
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div className={s.tableToolbar}>
        <div className={s.toolbarActionsLeft}>
          <>
            <PageInfoModal
              title="Aktiv Dərs"
              items={ACTIVE_LESSON_INFO_ITEMS}
            />
            <button
              className={s.iconActionBtnPurple}
              onClick={fetchStudents}
              title="Yenilə"
              style={{ marginLeft: 6 }}
            >
              <i className="bi bi-arrow-clockwise"></i>
            </button>

            <button
              className={s.iconActionBtnPurple}
              onClick={() => setViewingHistory(true)}
              disabled={!selectedStudentId}
              title="Keçmiş ballar"
              style={{
                marginLeft: 6,
                background: selectedStudentId
                  ? "#dbeafe"
                  : "transparent",
                opacity: selectedStudentId ? 1 : 0.4,
                border: selectedStudentId
                  ? "1px solid #2563eb"
                  : "1px solid transparent",
              }}
            >
              <i className="bi bi-clock-history"></i>
            </button>

            <button
              className={s.iconActionBtnPurple}
              onClick={() => {
                const st = students.find((s) => s.id === selectedStudentId);
                if (st) {
                  setPasswordStudent(st);
                  setShowPasswordModal(true);
                }
              }}
              disabled={!selectedStudentId}
              title="Şifrəni dəyiş"
              style={{
                marginLeft: 6,
                backgroundColor: selectedStudentId
                  ? "#fef3c7"
                  : "transparent",
                color: selectedStudentId ? "#f59e0b" : "#9ca3af",
                border: selectedStudentId
                  ? "1px solid #f59e0b"
                  : "1px solid #e5e7eb",
                opacity: selectedStudentId ? 1 : 0.4,
              }}
            >
              <i className="bi bi-key-fill"></i>
            </button>

            <button
              disabled={!selectedStudentId || activeFilter !== "Passiv"}
              onClick={() => {
                setExitReason("");
                setShowExitModal(true);
              }}
              title={
                activeFilter !== "Passiv"
                  ? "Yalnız Passiv filtrdə istifadə olunur"
                  : "Kursdan ayrılma səbəbi"
              }
              style={{
                marginLeft: 6,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: 6,
                fontSize: 14,
                cursor:
                  selectedStudentId && activeFilter === "Passiv"
                    ? "pointer"
                    : "not-allowed",
                backgroundColor:
                  selectedStudentId && activeFilter === "Passiv"
                    ? "#fee2e2"
                    : "transparent",
                color:
                  selectedStudentId && activeFilter === "Passiv"
                    ? "#ef4444"
                    : "#9ca3af",
                border:
                  selectedStudentId && activeFilter === "Passiv"
                    ? "1px solid #fca5a5"
                    : "1px solid #e5e7eb",
                opacity:
                  selectedStudentId && activeFilter === "Passiv" ? 1 : 0.4,
              }}
            >
              <i className="bi bi-person-dash-fill"></i>
            </button>

            <button
              disabled={!selectedStudentId}
              onClick={() => {
                setNotifyParentMessage("");
                setShowNotifyParentModal(true);
              }}
              title="Valideynə bildiriş göndər"
              style={{
                marginLeft: 6,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: 6,
                fontSize: 14,
                cursor: selectedStudentId ? "pointer" : "not-allowed",
                backgroundColor: selectedStudentId
                  ? "#dbeafe"
                  : "transparent",
                color: selectedStudentId ? "#3b82f6" : "#9ca3af",
                border: selectedStudentId
                  ? "1px solid #93c5fd"
                  : "1px solid #e5e7eb",
                opacity: selectedStudentId ? 1 : 0.4,
              }}
            >
              <i className="bi bi-megaphone-fill"></i>
            </button>

            <div className={s.pillGroup} style={{ marginLeft: 10 }}>
              {filterOptions.map((f) => (
                <button
                  key={f}
                  className={`${s.pillBtn} ${activeFilter === f ? s.pillActive : ""}`}
                  onClick={() => setActiveFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>
            <span
              style={{
                fontSize: 13,
                marginLeft: 10,
                color: "#374151",
                fontWeight: 500,
              }}
            >
              İştirak:{" "}
              <strong style={{ color: "#059669", fontSize: 13 }}>
                {Object.values(attendance).filter(Boolean).length}
              </strong>
              /{students.length}
            </span>
          </>
        </div>

        <div className={s.toolbarActionsRight}>
          <button
            onClick={openHelpModal}
            disabled={helpSelectedIds.size === 0}
            title="Seçilən tələbələr üçün kömək dərsi"
            style={{
              background: "#0ea5e9",
              color: "white",
              border: "1px solid #0284c7",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.2,
              cursor: helpSelectedIds.size === 0 ? "not-allowed" : "pointer",
              opacity: helpSelectedIds.size === 0 ? 0.5 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <i className="bi bi-person-raised-hand" /> Kömək dərsi
            {helpSelectedIds.size > 0 && (
              <span
                style={{
                  background: "#bae6fd",
                  color: "#0c4a6e",
                  borderRadius: 999,
                  padding: "0 6px",
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {helpSelectedIds.size}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowAttendanceConfirm(true)}
            disabled={saving}
            style={{
              background: "#2563EB",
              color: "white",
              border: "1px solid #2563EB",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.2,
              cursor: "pointer",
            }}
          >
            {saving ? (
              <AppSpinner inline />
            ) : (
              <>
                <i className="bi bi-send-fill"></i> Davamiyyəti Göndər
              </>
            )}
          </button>
          <button
            onClick={handleLessonDoneNotification}
            disabled={lessonDoneLoading}
            title="Dərsin bitməsi zamanı, bu düyməni istifadə edərək, bildiriş göndərmək lazımdır."
            style={{
              background: "#22c55e",
              color: "white",
              border: "1px solid #16a34a",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.2,
              cursor: "pointer",
            }}
          >
            {lessonDoneLoading ? (
              <AppSpinner inline />
            ) : (
              <>
                <i className="bi bi-bell-fill"></i> Dərs bitdi
              </>
            )}
          </button>
          <button
            onClick={() => setShowEndConfirm(true)}
            disabled={saving}
            title="Bu düymə, ümumi dərsi bitirir və məlumatlar qeydiyyat olunur."
            style={{
              background: "#ef4444",
              color: "white",
              border: "1px solid #dc2626",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.2,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? (
              <AppSpinner inline />
            ) : (
              <>
                <i className="bi bi-stop-fill"></i> Bitir
              </>
            )}
          </button>
          <button
            onClick={() => {
              localStorage.setItem(
                "activeLessonTableOrder",
                JSON.stringify(columns),
              );
              setSaveStatus(true);
              setTimeout(() => setSaveStatus(false), 2000);
            }}
            className={`btn-save-active ${saveStatus ? "active" : ""}`}
          >
            <i
              className={`bi bi-${saveStatus ? "check-lg" : "floppy-fill"}`}
            ></i>
          </button>
        </div>
      </div>

      <div
        ref={tableScrollRef}
        className={`${s.tableScrollContainer} tbl-scroll-wrap`}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={columns.map((c) => c.id)}
            strategy={horizontalListSortingStrategy}
          >
            <table
              style={{
                minWidth: "max-content",
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: 12,
                tableLayout: "auto",
              }}
            >
              <thead style={{ position: "sticky", top: 0, zIndex: 20 }}>
                <tr style={{ background: "var(--bg-elevated)" }}>
                  <th
                    style={{
                      padding: "6px 8px",
                      width: 32,
                      textAlign: "center",
                      background: "#f3f4f6",
                      borderBottom: "2px solid #d1d5db",
                    }}
                  >
                    <input
                      type="checkbox"
                      style={{ accentColor: "#0ea5e9", cursor: "pointer" }}
                      checked={
                        students.length > 0 &&
                        students.every((st) => helpSelectedIds.has(st.id))
                      }
                      onChange={(e) =>
                        setHelpSelectedIds(
                          e.target.checked
                            ? new Set(students.map((st) => st.id))
                            : new Set(),
                        )
                      }
                    />
                  </th>
                  {columns.map((col) => (
                    <SortableCol key={col.id} col={col}>
                      {col.label}
                    </SortableCol>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((st, idx) => (
                  <tr
                    key={st.id}
                    style={{
                      borderBottom: "1px solid #e5e7eb",
                      background: helpSelectedIds.has(st.id)
                        ? "rgba(14,165,233,0.08)"
                        : selectedStudentId === st.id
                          ? "rgba(37, 99, 235, 0.14)"
                          : attendance[st.id]
                            ? "rgba(5,150,105,0.07)"
                            : "#ffffff",
                      transition: "background 0.2s",
                    }}
                  >
                    <td
                      style={{
                        padding: "0 8px",
                        textAlign: "center",
                        width: 32,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        style={{ accentColor: "#0ea5e9", cursor: "pointer" }}
                        checked={helpSelectedIds.has(st.id)}
                        onChange={() => toggleHelpSelect(st.id)}
                      />
                    </td>
                    {columns.map((col) => renderCell(col.id, st, idx))}
                  </tr>
                ))}
              </tbody>
            </table>
          </SortableContext>
          <DragOverlay>
            {activeCol ? (
              <div
                className="p-2 bg-white border shadow"
                style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}
              >
                {activeCol.label}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
      <div
        ref={customScrollRef}
        onScroll={handleCustomScrollX}
        className="custom-x-scrollbar"
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          height: 10,
          flexShrink: 0,
        }}
      >
        <div style={{ width: tableScrollWidth, height: 1 }} />
      </div>

      {!viewingHistory && (
        <div
          style={{
            borderTop: "1px solid var(--border)",
            padding: "10px 14px",
            display: "flex",
            gap: 8,
            background: "var(--bg-elevated)",
            alignItems: "center",
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <input
            value={lessonName || lessonType}
            onChange={(e) => setLessonType(e.target.value)}
            placeholder="Dərs mövzusu..."
            style={{
              width: "180px",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              padding: "6px 10px",
              fontSize: 13,
              color: "#111827",
            }}
          />
          <input
            value={generalNote}
            onChange={(e) => setGeneralNote(e.target.value)}
            placeholder="Ümumi müəllim qeydi..."
            style={{
              flex: 1,
              minWidth: 120,
              border: "1px solid #d1d5db",
              borderRadius: 6,
              padding: "6px 10px",
              fontSize: 13,
              color: "#111827",
            }}
          />
        </div>
      )}

      <SelectionModal
        show={showModal}
        onHide={() => setShowModal(false)}
        student={modalStudent}
        onSelectMark={handleSelectMark}
        prevNotes={(prevLessonNotes[modalStudent?.id] || []).map((n) => n.text)}
      />

      <AttendanceConfirmModal
        show={showAttendanceConfirm}
        onHide={() => setShowAttendanceConfirm(false)}
        onConfirm={handleSaveMarks}
        loading={saving}
      />

      <EndLessonConfirmModal
        show={showEndConfirm}
        onHide={() => setShowEndConfirm(false)}
        onConfirm={handleEndLesson}
        loading={endingLesson}
      />

      <ValidationErrorModal
        show={!!errorModal}
        onHide={clearError}
        message={errorModal?.message}
        validations={errorModal?.validations}
      />

      <ValidationErrorModal
        show={!!helpErrorModal}
        onHide={() => setHelpErrorModal(null)}
        message={helpErrorModal?.message}
        validations={helpErrorModal?.validations}
      />

      {/* Kömək dərsi modalı */}
      {showHelpModal &&
        ReactDOM.createPortal(
          <div
            role="presentation"
            onClick={() => !helpLoading && closeHelpModal()}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 100050,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              background: "rgba(15, 23, 42, 0.45)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
                width: 800,
                maxWidth: "95vw",
                maxHeight: "85vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#f3f4f6",
                  borderBottom: "1px solid #e5e7eb",
                  padding: "12px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{ fontSize: 15, fontWeight: 700, color: "#374151" }}
                >
                  Kömək dərsi
                </span>
                {!helpLoading && (
                  <button
                    onClick={closeHelpModal}
                    style={{
                      border: "none",
                      background: "rgba(0,0,0,0.06)",
                      borderRadius: 6,
                      width: 28,
                      height: 28,
                      fontSize: 18,
                      color: "#6b7280",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              <div
                style={{
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  flex: 1,
                }}
              >
                <div
                  style={{
                    padding: "16px 20px 12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    flexShrink: 0,
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#374151",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        Başlama tarixi
                      </label>
                      <input
                        type="datetime-local"
                        value={helpBeginDate}
                        onChange={(e) => setHelpBeginDate(e.target.value)}
                        style={{
                          padding: "7px 10px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#374151",
                          outline: "none",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#374151",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        Bitmə tarixi
                      </label>
                      <input
                        type="datetime-local"
                        value={helpEndDate}
                        onChange={(e) => setHelpEndDate(e.target.value)}
                        style={{
                          padding: "7px 10px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#374151",
                          outline: "none",
                        }}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#374151",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        Dərs adı
                      </label>
                      <input
                        type="text"
                        value={helpLessonName}
                        onChange={(e) => setHelpLessonName(e.target.value)}
                        placeholder="Dərs mövzusu..."
                        style={{
                          padding: "7px 10px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#374151",
                          outline: "none",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#374151",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        Dərs növü
                      </label>
                      <select
                        value={helpLessonType}
                        onChange={(e) => setHelpLessonType(e.target.value)}
                        style={{
                          padding: "7px 10px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#374151",
                          outline: "none",
                          background: "#fff",
                        }}
                      >
                        <option value="Online">Online</option>
                        <option value="Offline">Offline</option>
                      </select>
                    </div>
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    <label
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#374151",
                        textTransform: "uppercase",
                        letterSpacing: "0.03em",
                      }}
                    >
                      YouTube linki
                    </label>
                    <input
                      type="text"
                      value={helpYoutubeLink}
                      onChange={(e) => setHelpYoutubeLink(e.target.value)}
                      placeholder="https://youtube.com/..."
                      style={{
                        padding: "7px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 12,
                        color: "#374151",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    overflowY: "auto",
                    padding: "12px 20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {helpSelectedStudents.map((st) => (
                    <div
                      key={st.id}
                      style={{
                        background: "#f8fafc",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "12px 14px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#374151",
                          marginBottom: 6,
                        }}
                      >
                        {st.name} {st.surname}
                        <span
                          style={{
                            fontSize: 11,
                            color: "#9ca3af",
                            fontWeight: 400,
                            marginLeft: 8,
                          }}
                        >
                          Müq. #{st.contractId}
                        </span>
                      </div>
                      <textarea
                        rows={2}
                        placeholder="Müəllim qeydi..."
                        value={helpNotes[st.contractId] || ""}
                        onChange={(e) =>
                          setHelpNotes((prev) => ({
                            ...prev,
                            [st.contractId]: e.target.value,
                          }))
                        }
                        style={{
                          width: "100%",
                          fontSize: 12,
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          padding: "6px 8px",
                          resize: "vertical",
                          outline: "none",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div
                style={{
                  background: "#f3f4f6",
                  borderTop: "1px solid #e5e7eb",
                  padding: "10px 16px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <button
                  onClick={closeHelpModal}
                  disabled={helpLoading}
                  style={{
                    background: "#fff",
                    color: "#374151",
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    padding: "7px 16px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Ləğv et
                </button>
                <button
                  onClick={handleHelpSubmit}
                  disabled={helpLoading || !helpBeginDate || !helpEndDate}
                  style={{
                    background: "#22c55e",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "7px 18px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor:
                      helpLoading || !helpBeginDate || !helpEndDate
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      helpLoading || !helpBeginDate || !helpEndDate ? 0.6 : 1,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {helpLoading ? (
                    <AppSpinner inline />
                  ) : (
                    <>
                      <i className="bi bi-check-lg" /> Yadda saxla
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <Modal
        show={showExitModal}
        onHide={() => setShowExitModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            <i className="bi bi-person-dash-fill me-2 text-danger"></i>
            Kursdan ayrılma səbəbi
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            const st = students.find((s) => s.id === selectedStudentId);
            return st ? (
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
                <i className="bi bi-person-fill me-1"></i>
                {st.name} {st.surname}
              </div>
            ) : null;
          })()}
          <textarea
            className="form-control"
            rows={4}
            placeholder="Kursdan ayrılma səbəbini yazın..."
            value={exitReason}
            onChange={(e) => setExitReason(e.target.value)}
            style={{ fontSize: 13, resize: "none" }}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer style={{ gap: 8 }}>
          <button
            onClick={() => setShowExitModal(false)}
            style={{
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              borderRadius: 8,
              padding: "6px 16px",
              fontSize: 13,
              cursor: "pointer",
              color: "#374151",
            }}
          >
            Ləğv et
          </button>
          <button
            onClick={handleSubmitExitReason}
            disabled={!exitReason.trim() || exitLoading}
            style={{
              background: "#ef4444",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "6px 16px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              opacity: !exitReason.trim() || exitLoading ? 0.5 : 1,
            }}
          >
            {exitLoading ? "Göndərilir..." : "Göndər"}
          </button>
        </Modal.Footer>
      </Modal>

      {showNotifyParentModal &&
        ReactDOM.createPortal(
          <div
            role="presentation"
            onClick={() => setShowNotifyParentModal(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 100050,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              background: "rgba(15, 23, 42, 0.45)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                border: "1.5px solid var(--color-primary)",
                borderRadius: 14,
                boxShadow:
                  "0 24px 64px rgba(37,99,235,0.22), 0 4px 16px rgba(0,0,0,0.14)",
                width: 500,
                maxWidth: "95vw",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "18px 22px 14px",
                  borderBottom: "1px solid #f3f4f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      background: "rgba(37,99,235,0.1)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className="bi bi-megaphone-fill"
                      style={{ fontSize: 16, color: "var(--color-primary)" }}
                    ></i>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--color-primary)",
                      }}
                    >
                      Valideynə bildiriş
                    </div>
                    {(() => {
                      const st = students.find(
                        (s) => s.id === selectedStudentId,
                      );
                      return st ? (
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            fontWeight: 500,
                            marginTop: 2,
                          }}
                        >
                          <i
                            className="bi bi-person-fill"
                            style={{ marginRight: 4, color: "#9ca3af" }}
                          ></i>
                          {st.name} {st.surname}
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowNotifyParentModal(false)}
                  style={{
                    border: "none",
                    background: "rgba(0,0,0,0.06)",
                    borderRadius: 6,
                    width: 28,
                    height: 28,
                    fontSize: 16,
                    color: "#6b7280",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ padding: "16px 22px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {PARENT_NOTIFY_PRESETS.map((preset) => {
                    const isSelected = notifyParentMessage.includes(preset);
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() =>
                          setNotifyParentMessage((prev) =>
                            isSelected
                              ? prev.filter((p) => p !== preset)
                              : [...prev, preset],
                          )
                        }
                        style={{
                          border: isSelected
                            ? "1.5px solid var(--color-primary)"
                            : "1px solid #e5e7eb",
                          borderRadius: 8,
                          padding: "8px 14px",
                          fontSize: 13,
                          fontWeight: isSelected ? 600 : 500,
                          background: isSelected
                            ? "rgba(37,99,235,0.1)"
                            : "#f9fafb",
                          color: isSelected ? "var(--color-primary)" : "#4b5563",
                          cursor: "pointer",
                          transition: "all 0.15s",
                          lineHeight: "20px",
                          textAlign: "left",
                        }}
                      >
                        {isSelected && (
                          <i
                            className="bi bi-check2 me-2"
                            style={{ fontSize: 12 }}
                          ></i>
                        )}
                        {preset}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div
                style={{
                  padding: "12px 22px",
                  borderTop: "1px solid #f3f4f6",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowNotifyParentModal(false)}
                  style={{
                    border: "1.5px solid #d1d5db",
                    background: "#f9fafb",
                    borderRadius: 7,
                    padding: "7px 18px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    color: "#374151",
                  }}
                >
                  Ləğv et
                </button>
                <button
                  type="button"
                  onClick={handleSendParentNotification}
                  disabled={!notifyParentMessage.length || notifyParentLoading}
                  style={{
                    border: "none",
                    borderRadius: 7,
                    padding: "7px 20px",
                    background:
                      !notifyParentMessage.length || notifyParentLoading
                        ? "rgba(37,99,235,0.4)"
                        : "var(--color-primary)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor:
                      !notifyParentMessage.length || notifyParentLoading
                        ? "not-allowed"
                        : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    transition: "background 0.15s",
                  }}
                >
                  {notifyParentLoading ? (
                    <AppSpinner inline />
                  ) : (
                    <>
                      <i className="bi bi-send-fill"></i> Göndər
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <Modal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>
            Şifrənin Yenilənməsi
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ChangePassword
            mode="student"
            targetData={passwordStudent}
            onComplete={() => setShowPasswordModal(false)}
            onApiError={handleApiError}
          />
        </Modal.Body>
      </Modal>

      {ReactDOM.createPortal(
        <ToastContainer
          position="bottom-end"
          className="p-3"
          style={{ zIndex: 200000 }}
        >
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              bg={toast.type === "success" ? "success" : "danger"}
              show
              autohide
              delay={2000}
            >
              <Toast.Header closeButton={false}>
                <strong className="me-auto">Sistem Mesajı</strong>
              </Toast.Header>
              <Toast.Body className="text-white">{toast.message}</Toast.Body>
            </Toast>
          ))}
        </ToastContainer>,
        document.body,
      )}

      <style>{`
        .help-modal-backdrop { background: #111827; }
        .btn-save-active { width: 32px; height: 32px; background: var(--color-primary); color: white; border: none; border-radius: 6px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .btn-save-active.active { background: #2563eb; }
        td { vertical-align: middle; background: #ffffff; }
        td:not([style*="white-space: normal"]) { white-space: nowrap; }
        table thead th { background: #f3f4f6 !important; color: #374151 !important; font-weight: 700 !important; border-bottom: 2px solid #d1d5db !important; font-size: 11px !important; }
        table tbody tr { border-bottom: 1px solid #e5e7eb !important; }
        table tbody tr:hover td { background: #eff6ff !important; }
        .tbl-scroll-wrap { scrollbar-width: thin; scrollbar-color: #93c5fd transparent; }
        .tbl-scroll-wrap::-webkit-scrollbar { width: 5px; height: 0; }
        .tbl-scroll-wrap::-webkit-scrollbar-track { background: transparent; }
        .tbl-scroll-wrap::-webkit-scrollbar-thumb { background: #93c5fd; border-radius: 4px; }
        .tbl-scroll-wrap::-webkit-scrollbar-thumb:hover { background: #60a5fa; }
        .custom-x-scrollbar { scrollbar-width: thin; scrollbar-color: #93c5fd #eff6ff; }
        .custom-x-scrollbar::-webkit-scrollbar { height: 6px; }
        .custom-x-scrollbar::-webkit-scrollbar-track { background: #eff6ff; border-radius: 3px; }
        .custom-x-scrollbar::-webkit-scrollbar-thumb { background: #93c5fd; border-radius: 3px; }
        .custom-x-scrollbar::-webkit-scrollbar-thumb:hover { background: #60a5fa; }
        @media (max-width: 768px) {
          table { font-size: 11px !important; }
          table thead th { font-size: 10px !important; padding: 6px 6px !important; }
          td { padding: 5px 6px !important; }
          select { font-size: 12px !important; padding: 3px 2px !important; }
        }
      `}</style>
    </div>
  );
}
